import { useEffect, useState, useCallback } from 'react'
import { useDarkMode } from '../App'
import { searchBooks, getAllBooks, PaginatedBooksResponse } from '../lib/api'
import Card from '../components/Card'
import SidePanel from '../components/SidePanel'
import EmailRequestForm from '../components/EmailRequestForm'
import { Item } from '../components/Card'
import { useTranslation } from '../i18n/translations'
import { useLanguage } from '../contexts/LanguageContext'

export default function Search(){
  const { isDark } = useDarkMode()
  const { t } = useTranslation()
  const { isRTL } = useLanguage()
  const [searchQuery, setSearchQuery] = useState('')
  const [isSearching, setIsSearching] = useState(false)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [searchResults, setSearchResults] = useState<Item[]>([])
  const [selectedFilter, setSelectedFilter] = useState<string>('All')
  const [selectedItem, setSelectedItem] = useState<Item | null>(null)
  const [pagination, setPagination] = useState<PaginatedBooksResponse | null>(null)
  const [currentPage, setCurrentPage] = useState(1)

  // Load all books initially (when component mounts)
  useEffect(() => {
    const loadAllBooks = async () => {
      setIsSearching(true)
      try {
        console.log('Loading all books initially...')
        const response = await getAllBooks(1, 12) // Start with page 1, 12 items
        console.log('All books loaded:', response)
        setSearchResults(response.books as Item[])
        setPagination(response)
        setCurrentPage(1)
      } catch (error) {
        console.error('Failed to load all books:', error)
        setSearchResults([])
      } finally {
        setIsSearching(false)
      }
    }

    loadAllBooks()
  }, [])

  // Load more books (pagination)
  const loadMoreBooks = useCallback(async () => {
    if (!pagination?.has_next || isLoadingMore) return

    setIsLoadingMore(true)
    try {
      const nextPage = currentPage + 1
      console.log('Loading more books, page:', nextPage)
      
      let response: PaginatedBooksResponse
      
      if (searchQuery.trim()) {
        // If there's a search query, use search API
        let bookType = ''
        if (selectedFilter === 'Books') {
          bookType = 'book'
        } else if (selectedFilter === 'Papers') {
          bookType = 'paper'
        }
        const searchResponse = await searchBooks(searchQuery, bookType, nextPage, 12)
        // Convert to our expected format since searchBooks returns different structure
        response = {
          books: searchResponse.books,
          total_count: searchResponse.pagination?.total_count || 0,
          page: searchResponse.pagination?.page || nextPage,
          per_page: searchResponse.pagination?.per_page || 12,
          total_pages: searchResponse.pagination?.total_pages || 1,
          has_next: searchResponse.pagination?.has_next || false,
          has_prev: searchResponse.pagination?.has_prev || false
        }
      } else {
        // If no search query, use getAllBooks
        response = await getAllBooks(nextPage, 12)
      }

      // Append new books to existing results
      setSearchResults(prev => [...prev, ...response.books as Item[]])
      setPagination(response)
      setCurrentPage(nextPage)
    } catch (error) {
      console.error('Failed to load more books:', error)
    } finally {
      setIsLoadingMore(false)
    }
  }, [pagination, isLoadingMore, currentPage, searchQuery, selectedFilter])

  // Perform search when query changes
  useEffect(() => {
    const performSearch = async () => {
      if (searchQuery.trim()) {
        setIsSearching(true)
        try {
          console.log('Performing search for:', searchQuery)
          
          // Determine book type filter
          let bookType = ''
          if (selectedFilter === 'Books') {
            bookType = 'book'
          } else if (selectedFilter === 'Papers') {
            bookType = 'paper'
          }

          console.log('Search parameters:', { query: searchQuery, bookType, filter: selectedFilter })
          const searchResponse = await searchBooks(searchQuery, bookType, 1, 12)
          console.log('Search results:', searchResponse)
          
          // Convert to our expected format
          const response: PaginatedBooksResponse = {
            books: searchResponse.books,
            total_count: searchResponse.pagination?.total_count || 0,
            page: searchResponse.pagination?.page || 1,
            per_page: searchResponse.pagination?.per_page || 12,
            total_pages: searchResponse.pagination?.total_pages || 1,
            has_next: searchResponse.pagination?.has_next || false,
            has_prev: searchResponse.pagination?.has_prev || false
          }
          
          // Apply additional filters that are not handled by the API
          let filteredResults = response.books
          if (selectedFilter === 'Open Access') {
            filteredResults = response.books.filter(item => item.license === 'OA')
          } else if (selectedFilter === 'Recent') {
            filteredResults = response.books.filter(item => item.year && item.year >= 2023)
          }

          console.log('Final filtered results:', filteredResults)
          setSearchResults(filteredResults as Item[])
          setPagination(response)
          setCurrentPage(1)
        } catch (error) {
          console.error('Search failed:', error)
          // For debugging, let's also show the error details
          if (error instanceof Error) {
            console.error('Error message:', error.message)
            console.error('Error stack:', error.stack)
          }
          setSearchResults([])
          setPagination(null)
        } finally {
          setIsSearching(false)
        }
      } else {
        // When search query is cleared, reload all books
        setIsSearching(true)
        try {
          console.log('Search query cleared, loading all books...')
          const response = await getAllBooks(1, 12)
          setSearchResults(response.books as Item[])
          setPagination(response)
          setCurrentPage(1)
        } catch (error) {
          console.error('Failed to load all books:', error)
          setSearchResults([])
          setPagination(null)
        } finally {
          setIsSearching(false)
        }
      }
    }

    // Debounce the search
    const timeoutId = setTimeout(performSearch, 300)
    return () => clearTimeout(timeoutId)
  }, [searchQuery, selectedFilter])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
  }

  const handleFilterClick = (filter: string) => {
    setSelectedFilter(filter)
  }

  // Get filter labels
  const getFilterLabel = (filter: string) => {
    switch (filter) {
      case 'All': return t('search.filter.all')
      case 'Books': return t('search.filter.books')
      case 'Papers': return t('search.filter.papers')
      case 'Open Access': return t('search.filter.open_access')
      case 'Recent': return t('search.filter.recent')
      default: return filter
    }
  }

  return (
    <div className={`min-h-screen w-full transition-colors ${isDark ? 'text-white' : 'text-gray-900'} ${isRTL ? 'font-mixed rtl' : ''}`} dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className={`absolute inset-0 transition-colors ${isDark ? 'bg-gradient-to-br from-emerald-900/10 to-cyan-900/10' : 'bg-gradient-to-br from-blue-50/30 to-purple-50/30'}`}></div>
        <div className="relative max-w-7xl mx-auto px-4 py-20">
          <div className={`text-center ${isRTL ? 'rtl' : ''}`}>
            <h1 className={`text-4xl md:text-5xl font-bold mb-6 transition-colors ${isDark ? 'bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent' : 'text-gray-900'} ${isRTL ? 'font-mixed' : ''}`}>
              {t('search.title')}
            </h1>
            <p className={`text-xl mb-12 max-w-2xl mx-auto transition-colors ${isDark ? 'text-gray-300' : 'text-gray-600'} ${isRTL ? 'text-center' : ''}`}>
              {t('search.subtitle')}
            </p>

            {/* Search Bar */}
            <div className="max-w-2xl mx-auto mb-16">
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('search.placeholder')}
                  dir={isRTL ? 'rtl' : 'ltr'}
                  style={isRTL ? { textAlign: 'right', direction: 'rtl' } : { textAlign: 'left', direction: 'ltr' }}
                  className={`w-full px-6 py-4 rounded-xl border focus:outline-none focus:ring-2 focus:border-transparent transition-all ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-400 focus:ring-emerald-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-blue-500'} ${isRTL ? 'text-right' : 'text-left'}`}
                />
              </form>
            </div>

            {/* Search Filters */}
            <div className={`flex flex-wrap justify-center gap-3 mb-12 ${isRTL ? 'flex-row-reverse' : ''}`}>
              {['All', 'Books', 'Papers', 'Open Access', 'Recent'].map((filter) => (
                <button
                  key={filter}
                  onClick={() => handleFilterClick(filter)}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-colors ${
                    selectedFilter === filter
                      ? (isDark ? 'bg-emerald-600 text-white' : 'bg-blue-600 text-white')
                      : (isDark
                        ? 'bg-gray-800 border border-gray-700 text-gray-300 hover:bg-gray-700'
                        : 'bg-white border border-gray-200 text-gray-700 hover:bg-gray-50')
                  }`}
                >
                  {getFilterLabel(filter)}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Search Results */}
      <div className="max-w-7xl mx-auto px-4 py-16">
        {/* Search Results Header */}
        {searchQuery ? (
          <div className={`mb-8 ${isRTL ? 'text-right' : ''}`}>
            <h2 className="text-2xl font-bold mb-4">{t('search.results_for')} "{searchQuery}"</h2>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {isSearching ? t('common.loading') : `${t('search.found_results')} ${pagination?.total_count || searchResults.length} result${(pagination?.total_count || searchResults.length) !== 1 ? 's' : ''}`}
            </p>
          </div>
        ) : (
          <div className={`mb-8 ${isRTL ? 'text-right' : ''}`}>
            <h2 className="text-2xl font-bold mb-4">Browse All Books</h2>
            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
              {isSearching ? t('common.loading') : `Showing ${searchResults.length} of ${pagination?.total_count || searchResults.length} items`}
            </p>
          </div>
        )}

        {/* Loading State */}
        {isSearching && searchResults.length === 0 && (
          <div className="flex justify-center items-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600"></div>
            <span className={`ml-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>{t('common.loading')}</span>
          </div>
        )}

        {/* Results Grid */}
        {searchResults.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              {searchResults.map((item) => (
                <Card key={`${item.slug}-${currentPage}`} item={item} onClick={() => setSelectedItem(item)} />
              ))}
            </div>

            {/* Load More Button */}
            {pagination?.has_next && (
              <div className="text-center">
                <button
                  onClick={loadMoreBooks}
                  disabled={isLoadingMore}
                  className={`inline-flex items-center px-6 py-3 rounded-lg font-medium transition-all ${
                    isDark 
                      ? 'bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-700 disabled:text-gray-400' 
                      : 'bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300 disabled:text-gray-500'
                  } ${isLoadingMore ? 'cursor-not-allowed' : 'cursor-pointer hover:transform hover:scale-105'}`}
                >
                  {isLoadingMore ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {t('common.loading')}
                    </>
                  ) : (
                    <>
                      Load More
                      <svg className={`w-4 h-4 ${isRTL ? 'mr-2 ml-0' : 'ml-2 mr-0'}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </>
                  )}
                </button>
              </div>
            )}
          </>
        )}

        {/* No Results */}
        {!isSearching && searchResults.length === 0 && (
          <div className={`text-center py-12 ${isRTL ? 'rtl' : ''}`}>
            <h3 className={`text-lg font-medium mb-2 ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
              {searchQuery ? t('search.no_results') : 'No books available'}
            </h3>
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
              {searchQuery ? t('search.no_results_desc') : 'Please check back later for new content'}
            </p>
          </div>
        )}
      </div>

      {/* Side Panel for Item Details */}
      <SidePanel open={!!selectedItem} onClose={() => setSelectedItem(null)} title={selectedItem?.title || ''}>
        {selectedItem && (
          <div className="space-y-4">
            {/* Cover Image */}
            {selectedItem.coverUrl && (
              <div className="w-full h-64 rounded-lg overflow-hidden">
                <img src={selectedItem.coverUrl} alt={selectedItem.title} className="w-full h-full object-cover"/>
              </div>
            )}

            {/* Metadata */}
            <div className={`space-y-2 ${isDark ? 'text-gray-300' : 'text-gray-600'} ${isRTL ? 'text-right' : ''}`}>
              <div className="text-sm">
                <strong>{t('item.authors')}:</strong> {selectedItem.authors?.join(', ') || 'Unknown'}
              </div>
              <div className="text-sm">
                <strong>{t('item.year')}:</strong> {selectedItem.year}
              </div>
              <div className="text-sm">
                <strong>{t('item.type')}:</strong> {selectedItem.type === 'book' ? t('common.book') : t('common.paper')}
              </div>
              <div className="text-sm">
                <strong>{t('item.license')}:</strong>
                <span className={`${isRTL ? 'mr-2' : 'ml-2'} px-2 py-0.5 rounded text-xs ${isDark ? 'bg-emerald-500/20 text-emerald-400' : 'bg-gray-100 text-gray-700'}`}>
                  {selectedItem.license}
                </span>
              </div>
            </div>

            {/* Description */}
            {selectedItem.description && (
              <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800/50' : 'bg-gray-50'} ${isRTL ? 'text-right' : ''}`}>
                <h3 className="font-semibold mb-2">{t('item.description')}</h3>
                <p className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-600'}`}>
                  {selectedItem.description}
                </p>
              </div>
            )}

            {/* Download Form */}
            <EmailRequestForm itemId={selectedItem.slug} />
          </div>
        )}
      </SidePanel>
    </div>
  )
}